import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import Pagination, { PAGE_SIZE, usePagedRows } from '../components/Pagination';
import { useToast } from '../components/Toast';
import { fetchAllPages } from '../api/fetchAll';
import {
  Listing,
  ListingStatus,
  listListings,
  setListingStatus,
} from '../api/listings';
import { extractApiError } from '../api/legalServices';

type Filter = 'all' | 'active' | 'inactive' | 'rental' | 'sale';

const FILTERS: Filter[] = ['all', 'active', 'inactive', 'rental', 'sale'];

const FILTER_LABEL: Record<Filter, string> = {
  all: 'Buy / Rent',
  active: 'Active',
  inactive: 'Inactive',
  rental: 'Rental',
  sale: 'Buy',
};

// Listings are stored as type 'Sale'; the admin UI calls them "Buy".
const TYPE_LABEL: Record<Listing['type'], string> = { Rental: 'Rental', Sale: 'Buy' };

const statusBadgeClass = (status: ListingStatus): string =>
  status === 'active' ? 'badge badge-active' : 'badge badge-rejected';

const formatDate = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatPrice = (price: Listing['price'], type: Listing['type']): string => {
  const n = typeof price === 'number' ? price : Number(price);
  if (!Number.isFinite(n)) return '—';
  const formatted = '$' + n.toLocaleString();
  return type === 'Rental' ? `${formatted}/mo` : formatted;
};

const Listings: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [busyId, setBusyId] = useState<number | null>(null);

  // Debounce the search box → 300ms.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput.trim().toLowerCase());
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // `status=any` is the admin-bypass mode in the backend's list endpoint.
      // Every listing: the filters, search and counts below run in the browser.
      const all = await fetchAllPages((page, limit) =>
        listListings({ status: 'any', page, limit }).then((res) => ({
          rows: res.listings,
          totalPages: res.totalPages,
        }))
      );
      setListings(all);
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load listings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return listings.filter((l) => {
      // Filter by status / type chip.
      let matchFilter = true;
      if (filter === 'rental') matchFilter = l.type === 'Rental';
      else if (filter === 'sale') matchFilter = l.type === 'Sale';
      else if (filter !== 'all') matchFilter = l.status === filter;

      if (!matchFilter) return false;

      // Free-text search across address + agent name.
      if (!search) return true;
      const haystack =
        (l.address || '').toLowerCase() +
        ' ' +
        (l.title || '').toLowerCase() +
        ' ' +
        (l.agent?.name || '').toLowerCase() +
        ' ' +
        (l.agent?.email || '').toLowerCase();
      return haystack.includes(search);
    });
  }, [listings, filter, search]);

  const applyStatus = async (l: Listing, next: ListingStatus) => {
    setBusyId(l.id);
    // Optimistic update.
    setListings((prev) =>
      prev.map((x) => (x.id === l.id ? { ...x, status: next } : x))
    );
    try {
      const updated = await setListingStatus(l.id, next);
      // Merge instead of replace so optional fields (like the joined `agent`)
      // are preserved if the server response omits them.
      setListings((prev) =>
        prev.map((x) => (x.id === l.id ? { ...x, ...updated } : x))
      );
      toast(
        `"${updated.title}" ${next === 'active' ? 'activated' : 'deactivated'}`,
        next === 'active' ? 'success' : 'info'
      );
    } catch (err) {
      // Roll back.
      setListings((prev) =>
        prev.map((x) => (x.id === l.id ? { ...x, status: l.status } : x))
      );
      toast(extractApiError(err, 'Failed to update listing'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  // Summary counts for the stats row.
  const stats = useMemo(() => {
    const acc = { total: listings.length, active: 0, inactive: 0 };
    for (const l of listings) {
      if (l.status === 'active') acc.active++;
      else acc.inactive++;
    }
    return acc;
  }, [listings]);

  const { page, setPage, pageRows, total } = usePagedRows(filtered, PAGE_SIZE, `${filter}|${search}`);

  return (
    <div>
      {/* Stats row */}
      <div className="flex gap-4 flex-wrap mb-6">
        <div className="card flex-1 min-w-[200px] p-5 animate-fadeIn">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--brand-bg)' }}
            >
              <Icon name="building-2" size={20} color="var(--brand)" />
            </div>
            <div className="text-[13px] text-g500 font-medium">Total</div>
          </div>
          <div className="text-[26px] font-extrabold text-ink leading-tight">
            {stats.total}
          </div>
          <div className="text-[12px] text-g400 mt-1">all statuses</div>
        </div>
        <div className="card flex-1 min-w-[200px] p-5 animate-fadeIn">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--green-bg)' }}
            >
              <Icon name="check-circle" size={20} color="var(--green)" />
            </div>
            <div className="text-[13px] text-g500 font-medium">Active</div>
          </div>
          <div className="text-[26px] font-extrabold text-ink leading-tight">
            {stats.active}
          </div>
          <div className="text-[12px] text-g400 mt-1">visible to public</div>
        </div>
        <div className="card flex-1 min-w-[200px] p-5 animate-fadeIn">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--g100)' }}
            >
              <Icon name="x-circle" size={20} color="var(--g500)" />
            </div>
            <div className="text-[13px] text-g500 font-medium">Inactive</div>
          </div>
          <div className="text-[26px] font-extrabold text-ink leading-tight">
            {stats.inactive}
          </div>
          <div className="text-[12px] text-g400 mt-1">hidden from public</div>
        </div>
      </div>

      {/* Search + filter row */}
      <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
        <div className="relative flex-1 max-w-[380px]">
          <span className="absolute left-3 top-1/2 -translate-y-1/2">
            <Icon name="search" size={18} color="var(--g400)" />
          </span>
          <input
            className="form-input"
            style={{ paddingLeft: 40 }}
            placeholder="Search by address, title, or merchant..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {FILTERS.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="capitalize"
                style={{
                  padding: '8px 16px',
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  border:
                    '1px solid ' + (active ? 'var(--brand)' : 'var(--g200)'),
                  background: active ? 'var(--brand-light)' : '#fff',
                  color: active ? 'var(--brand)' : 'var(--g600)',
                  transition: 'all .15s ease',
                }}
              >
                {FILTER_LABEL[f]}
              </button>
            );
          })}
          <button
            className="btn btn-primary"
            onClick={() => navigate('/admin/listings/new')}
          >
            <Icon name="plus" size={16} color="#fff" /> Add New Listing
          </button>
        </div>
      </div>

      {/* Load error */}
      {loadError && (
        <div
          className="card p-5 mb-5"
          role="alert"
          style={{
            background: 'var(--red-bg)',
            color: 'var(--red)',
            border: '1px solid rgba(220,38,38,.12)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="x-circle" size={18} color="var(--red)" />
            <strong>Couldn't load listings</strong>
          </div>
          <div className="text-sm mb-3">{loadError}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {/* Table */}
      <div
        className="bg-white rounded-card overflow-hidden border border-g100"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}
      >
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {[
                'Merchant',
                'Property Address',
                'Type',
                'Price',
                'Status',
                'Date Added',
                'Actions',
              ].map((h, i) => (
                <th
                  key={h}
                  className={
                    'px-4 py-3 text-left text-xs font-semibold text-g500 uppercase tracking-wider border-b border-g200 bg-g50 whitespace-nowrap' +
                    (i === 6 ? ' text-right' : '')
                  }
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7}>
                  <div className="py-12 text-center text-g500">
                    <Icon name="building-2" size={28} color="var(--g400)" />{' '}
                    Loading listings...
                  </div>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div
                      className="w-[72px] h-[72px] rounded-[20px] flex items-center justify-center mb-5"
                      style={{ background: 'var(--g100)' }}
                    >
                      <Icon name="building-2" size={32} color="var(--g400)" />
                    </div>
                    <div className="text-[17px] font-semibold text-ink mb-1.5">
                      No listings found
                    </div>
                    <div className="text-sm text-g500 max-w-[340px]">
                      {search || filter !== 'all'
                        ? 'Try adjusting your filters or search.'
                        : 'Once merchants start publishing listings, they will show up here.'}
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              pageRows.map((l) => {
                const isBusy = busyId === l.id;
                return (
                  <tr
                    key={l.id}
                    className="hover:bg-g50 transition-colors"
                    style={{ opacity: isBusy ? 0.7 : 1 }}
                  >
                    <td className="px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b border-g100">
                      {l.agent?.name || (
                        <span className="text-g400 italic">Unknown</span>
                      )}
                      {l.agent?.email && (
                        <div className="text-xs text-g500 font-normal">
                          {l.agent.email}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                      <span className="flex items-center gap-2">
                        <Icon name="map-pin" size={14} color="var(--g400)" />
                        <span>
                          <div className="font-medium">{l.title}</div>
                          <div className="text-xs text-g500">{l.address}</div>
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                      <span
                        className={`type-badge type-${l.type.toLowerCase()}`}
                      >
                        {TYPE_LABEL[l.type] ?? l.type}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b border-g100">
                      {formatPrice(l.price, l.type)}
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                      <span className={statusBadgeClass(l.status)}>
                        {l.status[0].toUpperCase() + l.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-g500 whitespace-nowrap border-b border-g100">
                      {formatDate(l.createdAt)}
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100 text-right">
                      <div className="flex gap-1.5 justify-end">
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => navigate(`/admin/listings/${l.id}`)}
                          title="View details"
                        >
                          <Icon name="eye" size={14} /> Details
                        </button>
                        {l.status === 'active' ? (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => applyStatus(l, 'inactive')}
                            disabled={isBusy}
                            title="Hide from public"
                          >
                            <Icon name="x-circle" size={14} /> Deactivate
                          </button>
                        ) : (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => applyStatus(l, 'active')}
                            disabled={isBusy}
                          >
                            <Icon name="check-circle" size={14} color="#fff" />{' '}
                            Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} disabled={loading} />
    </div>
  );
};

export default Listings;
